import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  TextField, Grid, Typography, Button, Container, Select as MuiSelect, MenuItem, Snackbar, Alert,
  IconButton, Paper, Dialog, DialogActions, DialogContent, DialogTitle, Tooltip, CircularProgress,
  Box, Stack, FormControl, InputLabel, useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Add, Settings, Sms, DriveFileMove } from "@mui/icons-material";
import getNextSaturday from "./dateUtils";
import ImportGamesModal from "./ImportGamesModal";
import GamesTable from "./GamesTable";
import GameCards from "./GameCards";
import GameFormDialog, { EMPTY_TEAM } from "./GameFormDialog";
import MoveGamesDialog from "./MoveGamesDialog";
import useAdminWeek, { MAX_WEEK } from "./useAdminWeek";
import { db } from '../../../firebase';
import { getFunctions, httpsCallable } from "firebase/functions";
import { useData } from "../../../contexts/DataContext";
import { Timestamp, collection, doc, getDocs, setDoc, addDoc, deleteDoc, updateDoc, writeBatch } from "firebase/firestore";
import { sanitizeScoreInput, isValidScore } from "../../../lib/scoreValidation";
import { normalizePicks } from "../../../lib/normalizePicks";
import { reorder, sortReviewGames } from "../../../lib/gameOrder";

const WEEK_OPTIONS = [...Array(MAX_WEEK).keys()].map((i) => i + 1);

const emptyGameForm = (week) => ({
  week,
  team1: { ...EMPTY_TEAM },
  team2: { ...EMPTY_TEAM },
  gameDate: getNextSaturday(),
  gameTime: "",
  isPickScore: false,
  awardWinnerBonus: false,
  bettingLine: "",
  neutralSite: "",
  displayOrder: 0,
});

// Wraps a disabled button so its tooltip still shows. Block-level so a
// full-width button inside it can actually stretch.
const Hint = ({ title, children }) => (
  <Tooltip title={title || ''}>
    <span style={{ display: 'block' }}>{children}</span>
  </Tooltip>
);

function Admin() {
  const { getAllSeasons, currentSeason, setAppSeason, setCurrentWeek: setCurrentWeekInDb, getCurrentWeek } = useData();
  const theme = useTheme();
  // Same breakpoint as the header drawer and the leaderboard's phone view.
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // The working season/week (what this page and Scoring operate on) lives in
  // the URL. The week players see is separate: games/{season}.currentWeek.
  const { season: urlSeason, week, setSeason, setWeek, setSelection } = useAdminWeek();
  const [seasons, setSeasons] = useState([]);
  const selectedSeason = urlSeason || "";
  const [activeWeek, setActiveWeek] = useState(null);
  // What the page is showing right now. Async loads compare against this
  // before writing state, so a slow read for a week the admin has already
  // left can't overwrite the week they're on.
  const selectionRef = useRef({ season: selectedSeason, week });
  selectionRef.current = { season: selectedSeason, week };

  const [teams, setTeams] = useState([]);
  const [games, setGames] = useState([]);
  const [isDataLoading, setIsDataLoading] = useState(true);

  // Add/Edit game dialog
  const [formMode, setFormMode] = useState(null); // null | 'add' | 'edit'
  const [gameData, setGameData] = useState(emptyGameForm(""));
  const [editGameId, setEditGameId] = useState(null);
  const [isSavingGame, setIsSavingGame] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [scores, setScores] = useState({}); // To hold scores for the games list
  // Delete is destructive and one tap away in the games table, so it asks first.
  const [gameToDelete, setGameToDelete] = useState(null);
  const [isDeletingGame, setIsDeletingGame] = useState(false);
  const [isScoreModalOpen, setIsScoreModalOpen] = useState(false);
  const [selectedGameForScore, setSelectedGameForScore] = useState(null);
  const [oddsData, setOddsData] = useState([]);
  const [isFetchingScores, setIsFetchingScores] = useState(false);
  const [isUpdatingOdds, setIsUpdatingOdds] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  // Games ticked for "Move to Week", by id. Only ever games of the week shown.
  const [selectedGameIds, setSelectedGameIds] = useState(() => new Set());
  const [isMoveDialogOpen, setIsMoveDialogOpen] = useState(false);
  const [isNotifyConfirmOpen, setIsNotifyConfirmOpen] = useState(false);
  const [isNotifyingNewGames, setIsNotifyingNewGames] = useState(false);
  const [isSavingScores, setIsSavingScores] = useState(false);
  const [isSortingGames, setIsSortingGames] = useState(false);

  // Season settings + "Set as active"
  const [isSeasonSettingsOpen, setIsSeasonSettingsOpen] = useState(false);
  const [newSeasonInput, setNewSeasonInput] = useState("");
  const [isCreatingSeason, setIsCreatingSeason] = useState(false);
  const [isSetActiveConfirmOpen, setIsSetActiveConfirmOpen] = useState(false);
  const [isSettingActive, setIsSettingActive] = useState(false);

  // Optional { label, onClick } shown as a button in the message.
  const [snackbarAction, setSnackbarAction] = useState(null);
  const notify = (message, severity = "success", action = null) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarAction(action);
    setSnackbarOpen(true);
  };

  // The odds/score/notify callables send only the week; the server resolves
  // the season from config/app. On any other season those buttons would act
  // on the active season while appearing to act on this one, so they're off.
  const isActiveSeason = Boolean(selectedSeason) && selectedSeason === currentSeason;
  const isActiveSelection = isActiveSeason && Boolean(week) && week === activeWeek;
  const activeSeasonHint = isActiveSeason ? '' :
    `Acts on the active season (${currentSeason ?? '…'}) only. Switch to it, or set ${selectedSeason || 'this season'} as active first.`;

  // Fetch seasons on initial load
  useEffect(() => {
    const fetchSeasons = async () => {
      setSeasons(await getAllSeasons());
    };
    fetchSeasons();
  }, [getAllSeasons]);

  // No season in the URL, or one that doesn't exist: fall back to the active
  // season (else the first one) and write it back.
  useEffect(() => {
    if (seasons.length === 0) return;
    const unknown = selectedSeason && !seasons.includes(selectedSeason);
    if (!selectedSeason || unknown) {
      setSeason(seasons.includes(currentSeason) ? currentSeason : seasons[0]);
    }
  }, [seasons, selectedSeason, currentSeason, setSeason]);

  // The week players currently see, always read for the *active* season.
  useEffect(() => {
    if (!currentSeason) return;
    const fetchActiveWeek = async () => {
      setActiveWeek(await getCurrentWeek(currentSeason));
    };
    fetchActiveWeek();
  }, [currentSeason, getCurrentWeek]);

  useEffect(() => {
    const fetchTeams = async () => {
      const teamsCollection = await getDocs(collection(db, "teams"));
      setTeams(teamsCollection.docs.map((d) => ({ id: d.id, ...d.data() })));
    };
    fetchTeams();
  }, []);

  const loadGames = useCallback(async (season, targetWeek) => {
    const weekDocRef = collection(db, 'games', season, targetWeek.toString());
    const gamesSnapshot = await getDocs(weekDocRef);
    return gamesSnapshot.docs.map((d) => ({
      id: d.id,
      week: targetWeek, // Add the week number to each game
      ...d.data(),
    })).sort((a, b) => a.displayOrder - b.displayOrder);
  }, []);

  const applyGames = useCallback((gamesData) => {
    setGames(gamesData);
    const initialScores = {};
    gamesData.forEach((game) => {
      initialScores[game.id] = {
        homeScore: game.homeScore || "",
        awayScore: game.awayScore || "",
      };
    });
    setScores(initialScores);
  }, []);

  const isCurrentSelection = (season, targetWeek) =>
    selectionRef.current.season === season && String(selectionRef.current.week) === String(targetWeek);

  // Re-reads a week after a write. Applied only if the admin is still on it.
  const fetchGames = useCallback(async (season, targetWeek) => {
    if (!season || !targetWeek) return;
    const gamesData = await loadGames(season, targetWeek);
    if (isCurrentSelection(season, targetWeek)) applyGames(gamesData);
  }, [loadGames, applyGames]);

  // With a week in the URL, load it. Without one, find the week to work on:
  // the first week with a game still to be played, else the week after the
  // last one that has games. That goes into the URL, which re-runs this.
  useEffect(() => {
    if (!selectedSeason) return;
    let cancelled = false;
    let failed = false;
    const load = async () => {
      setIsDataLoading(true);
      try {
        if (week) {
          const gamesData = await loadGames(selectedSeason, week);
          if (!cancelled) applyGames(gamesData);
          return;
        }
        const weekSnapshots = await Promise.all(
          WEEK_OPTIONS.map((i) => getDocs(collection(db, 'games', selectedSeason, i.toString())))
        );
        const now = Date.now();
        let detected = 1;
        let lastWeekWithGames = 0;
        let found = false;
        weekSnapshots.forEach((snapshot, index) => {
          if (snapshot.empty) return;
          lastWeekWithGames = index + 1;
          if (found) return;
          const hasUpcomingGame = snapshot.docs.some((d) => {
            const game = d.data();
            return game.startTime && game.startTime.seconds * 1000 >= now;
          });
          if (hasUpcomingGame) {
            detected = index + 1;
            found = true;
          }
        });
        if (!found && lastWeekWithGames > 0) detected = lastWeekWithGames + 1;
        if (!cancelled) setWeek(detected);
      } catch (error) {
        failed = true;
        console.error("Error fetching initial data: ", error);
        if (!cancelled) notify(`Couldn't load games: ${error.message || 'unknown error'}`, "error");
      } finally {
        // The detection branch hands off to a re-run with a week in the URL,
        // so it only clears the spinner itself when that hand-off won't come.
        if (!cancelled && (week || failed)) setIsDataLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [selectedSeason, week, loadGames, applyGames, setWeek]);

  // A selection belongs to the week it was made in.
  useEffect(() => { setSelectedGameIds(new Set()); }, [selectedSeason, week]);
  useEffect(() => {
    setSelectedGameIds((prev) => {
      const ids = new Set(games.map((g) => g.id));
      const kept = [...prev].filter((id) => ids.has(id));
      return kept.length === prev.size ? prev : new Set(kept);
    });
  }, [games]);

  const toggleGameSelected = (id) => {
    setSelectedGameIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleGamesMoved = ({ toWeek, moved, playersWithPicks }) => {
    setIsMoveDialogOpen(false);
    setSelectedGameIds(new Set());
    const n = moved.length;
    // Stay on this week (cleanups often move several batches out of it), with
    // a one-tap way to check the week the games went to.
    notify(
      `Moved ${n} game${n === 1 ? '' : 's'} to Week ${toWeek}` +
        (playersWithPicks ? `, with ${playersWithPicks} player${playersWithPicks === 1 ? "'s" : "s'"} picks.` : '.'),
      "success",
      { label: `View Week ${toWeek}`, onClick: () => { setSnackbarOpen(false); setWeek(toWeek); } }
    );
    fetchGames(selectedSeason, week);
  };

  // ── Season settings / active selection ──────────────────────────────────

  const handleCreateSeason = async () => {
    const year = newSeasonInput.trim();
    if (!year || !/^\d{4}$/.test(year)) {
      notify("Please enter a valid 4-digit year.", "error");
      return;
    }
    if (seasons.includes(year)) {
      notify(`Season ${year} already exists.`, "warning");
      return;
    }
    setIsCreatingSeason(true);
    try {
      // Create the season doc in Firestore with currentWeek: 1, make it the
      // season players see, and start working in its week 1.
      await setDoc(doc(db, 'games', year), { currentWeek: 1 });
      await setAppSeason(year);
      setSeasons((prev) => [...prev, year].sort());
      setNewSeasonInput("");
      setIsSeasonSettingsOpen(false);
      setSelection(year, 1);
      notify(`Season ${year} created and set as the active season.`);
    } catch (error) {
      console.error("Error creating season:", error);
      notify(`Failed to create season: ${error.message}`, "error");
    } finally {
      setIsCreatingSeason(false);
    }
  };

  const handleSetActive = async () => {
    setIsSettingActive(true);
    // Week first: if it fails, nothing players see has changed. If the season
    // flip then fails, only a non-active season's week doc moved.
    let stage = 'week';
    try {
      await setCurrentWeekInDb(selectedSeason, week);
      if (selectedSeason !== currentSeason) {
        stage = 'season';
        await setAppSeason(selectedSeason);
      }
      setActiveWeek(week);
      setIsSetActiveConfirmOpen(false);
      notify(`Players now see Week ${week} of ${selectedSeason}.`);
    } catch (error) {
      console.error("Error setting active season/week:", error);
      notify(stage === 'week'
        ? `Failed to set the active week; nothing changed for players. ${error.message}`
        : `Week ${week} was saved for ${selectedSeason}, but making it the active season failed; players still see ${currentSeason}. ${error.message}`, "error");
    } finally {
      setIsSettingActive(false);
    }
  };

  // ── Add / Edit game ─────────────────────────────────────────────────────

  const openAddGame = () => {
    setEditGameId(null);
    setGameData({ ...emptyGameForm(week), displayOrder: games.length });
    setFormMode('add');
  };

  const handleEditGame = (game) => {
    const startDate = new Date(game.startTime.seconds * 1000);
    // Format date as YYYY-MM-DD (local)
    const year = startDate.getFullYear();
    const month = String(startDate.getMonth() + 1).padStart(2, '0');
    const day = String(startDate.getDate()).padStart(2, '0');
    const localDate = `${year}-${month}-${day}`;
    // Format time as HH:MM (local)
    const hours = String(startDate.getHours()).padStart(2, '0');
    const minutes = String(startDate.getMinutes()).padStart(2, '0');
    const localTime = `${hours}:${minutes}`;

    setEditGameId(game.id);
    setGameData({
      week: game.week,
      team1: {
        id: game.awayTeam,
        name: teams.find((team) => team.id === game.awayTeam)?.name || "",
        ranking: game.awayRanking,
        record: game.awayRecord,
      },
      team2: {
        id: game.homeTeam,
        name: teams.find((team) => team.id === game.homeTeam)?.name || "",
        ranking: game.homeRanking,
        record: game.homeRecord,
      },
      gameDate: localDate,
      gameTime: localTime,
      isPickScore: game.isPickScore,
      awardWinnerBonus: game.awardWinnerBonus || false,
      bettingLine: game.vegasLine,
      neutralSite: game.neutralSite,
      displayOrder: game.displayOrder,
    });
    setFormMode('edit');
  };

  const closeGameForm = () => {
    setFormMode(null);
    setEditGameId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isEditing = formMode === 'edit';

    // A new game goes in the working week; an edit stays in the game's week.
    if (!gameData.week) {
      notify("Please select a valid week.", "error");
      return;
    }
    if (!gameData.team1.id || !gameData.team2.id) {
      notify("Pick both teams first.", "error");
      return;
    }
    // An empty time used to produce an invalid kickoff timestamp that broke
    // every read of the game's start time.
    if (!gameData.gameDate || !gameData.gameTime) {
      notify("Enter the game date and time.", "error");
      return;
    }

    setIsSavingGame(true);
    try {
      // Combine gameDate and gameTime into a single timestamp
      const dateParts = gameData.gameDate.split('-');
      const gameDate = new Date(dateParts[0], dateParts[1] - 1, dateParts[2]);
      const gameTime = gameData.gameTime.split(":");
      gameDate.setHours(gameTime[0]);
      gameDate.setMinutes(gameTime[1]);

      const weekCollectionRef = collection(db, 'games', selectedSeason, gameData.week.toString());

      // Guard: don't flip a game's scoring type once real picks exist for it.
      // Stored picks keep their submission-time shape, so changing the type would
      // score them inconsistently. The admin must clear those picks first.
      if (isEditing) {
        const originalGame = games.find((g) => g.id === editGameId);
        if (originalGame && !!originalGame.isPickScore !== !!gameData.isPickScore) {
          const picksSnap = await getDocs(collection(db, 'picks', selectedSeason, gameData.week.toString()));
          const hasPick = picksSnap.docs.some((d) => {
            const p = normalizePicks(d.data().picks, games)[editGameId];
            return !!p && (
              p.homeWin === true || p.awayWin === true ||
              (p.homeScore !== undefined && p.homeScore !== '') ||
              (p.awayScore !== undefined && p.awayScore !== '')
            );
          });
          if (hasPick) {
            notify("Can't change scoring type — picks already exist for this game. Clear those picks first.", "error");
            return;
          }
        }
      }

      const homeTeam = teams.find((t) => t.id === gameData.team2.id);
      const awayTeam = teams.find((t) => t.id === gameData.team1.id);

      const gameOdds = (oddsData || []).find((o) => o.home_team === homeTeam?.apiTeamName && o.away_team === awayTeam?.apiTeamName);

      const dataToSave = {
        awayTeam: gameData.team1.id,
        awayRanking: gameData.team1.ranking,
        awayRecord: gameData.team1.record,
        homeTeam: gameData.team2.id,
        homeRanking: gameData.team2.ranking,
        homeRecord: gameData.team2.record,
        startTime: Timestamp.fromDate(gameDate),
        isPickScore: gameData.isPickScore,
        awardWinnerBonus: gameData.isPickScore ? gameData.awardWinnerBonus : false,
        vegasLine: gameData.bettingLine,
        neutralSite: gameData.neutralSite,
        displayOrder: isEditing ? gameData.displayOrder : games.length,
        week: gameData.week,
      };
      // The odds-feed id is only known when this page has fresh odds loaded.
      // Without a match, a new game gets null and an edited game keeps the id
      // it already has (overwriting it with null broke the importer's
      // already-imported check for that game).
      if (gameOdds) {
        dataToSave.apiGameId = gameOdds.id;
      } else if (!isEditing) {
        dataToSave.apiGameId = null;
      }

      if (isEditing) {
        // Update only the fields this form owns. A full overwrite (setDoc)
        // erased everything the form doesn't carry — final scores entered via
        // the score editor or Fetch Scores, the odds-feed id, and any future
        // fields — every time a game's details were edited.
        await updateDoc(doc(weekCollectionRef, editGameId), dataToSave);
        notify("Game updated successfully!");
      } else {
        // Create a new game with a Firestore-generated id (stable + never
        // reused, so picks keyed by game id never collide with a later create).
        await addDoc(weekCollectionRef, dataToSave);
        notify("Game saved successfully!");
      }

      fetchGames(selectedSeason, gameData.week);
      closeGameForm();
    } catch (error) {
      console.error("Error saving game: ", error);
      notify("Error saving game. Please try again.", "error");
    } finally {
      setIsSavingGame(false);
    }
  };

  // ── Delete ──────────────────────────────────────────────────────────────

  const handleDeleteGame = async (game) => {
    setIsDeletingGame(true);
    try {
      await deleteDoc(doc(db, 'games', selectedSeason, game.week.toString(), game.id));
      notify("Game deleted successfully!");
      setGames(games.filter((g) => g.id !== game.id));
      setGameToDelete(null);
    } catch (error) {
      console.error("Error deleting game: ", error);
      notify("Error deleting game. Please try again.", "error");
    } finally {
      setIsDeletingGame(false);
    }
  };

  // ── Scores ──────────────────────────────────────────────────────────────

  const handleScoreChange = (gameId, team, value) => {
    setScores((prevScores) => ({
      ...prevScores,
      [gameId]: {
        ...prevScores[gameId],
        [team]: sanitizeScoreInput(value),
      },
    }));
  };

  const handleSaveScores = async () => {
    if (!selectedGameForScore) return;

    const gameRef = doc(db, 'games', selectedSeason, selectedGameForScore.week.toString(), selectedGameForScore.id);
    const gameScores = scores[selectedGameForScore.id];

    setIsSavingScores(true);
    try {
      await updateDoc(gameRef, {
        homeScore: gameScores.homeScore,
        awayScore: gameScores.awayScore,
      });
      notify("Scores saved successfully!");
      setIsScoreModalOpen(false);
      fetchGames(selectedSeason, week); // Refresh games to show new scores
    } catch (error) {
      console.error("Error saving scores: ", error);
      notify("Error saving scores. Please try again.", "error");
    } finally {
      setIsSavingScores(false);
    }
  };

  const openScoreModal = (game) => {
    setSelectedGameForScore(game);
    setIsScoreModalOpen(true);
  };

  const closeScoreModal = () => {
    setIsScoreModalOpen(false);
    setSelectedGameForScore(null);
  };

  // ── Ordering ────────────────────────────────────────────────────────────

  // Writes each game's list position as its displayOrder, which is the order
  // players see the week in.
  const persistDisplayOrder = async (items) => {
    const batch = writeBatch(db);

    items.forEach((item, index) => {
      const gameRef = doc(db, 'games', selectedSeason, item.week.toString(), item.id);
      batch.update(gameRef, { displayOrder: index });
    });

    try {
      await batch.commit();
      // No need to re-fetch, just update the state
      setGames(items.map((item, index) => ({ ...item, displayOrder: index })));
      return true;
    } catch (error) {
      console.error("Error updating display order: ", error);
      notify("Error updating display order. Please try again.", "error");
      return false;
    }
  };

  const onDragEnd = (result) => {
    if (!result.destination) {
      return;
    }

    persistDisplayOrder(reorder(games, result.source.index, result.destination.index));
  };

  // Same standard order as the importer's review step: pick-the-score games,
  // then Top 25 matchups, then the rest, each by kickoff.
  const handleAutoSortGames = async () => {
    const sorted = sortReviewGames(games);
    const unchanged = sorted.every((game, index) =>
      game.id === games[index].id && game.displayOrder === index);
    if (unchanged) {
      notify("Games are already in standard order.", "info");
      return;
    }

    setIsSortingGames(true);
    try {
      if (await persistDisplayOrder(sorted)) {
        notify("Games sorted.");
      }
    } finally {
      setIsSortingGames(false);
    }
  };

  // ── API actions ─────────────────────────────────────────────────────────

  const handleRefreshGameData = async () => {
    setIsUpdatingOdds(true);
    try {
      const updateOdds = httpsCallable(getFunctions(), 'updateVegasOdds');
      const result = await updateOdds({ week });
      if (result.data.success) {
        setOddsData(result.data.oddsData || []);
        fetchGames(selectedSeason, week);
      }
      const unmapped = result.data.unconfiguredTeams || [];
      notify(result.data.message || "Odds update triggered successfully!", unmapped.length > 0 ? "warning" : "success");
    } catch (error) {
      console.error("Error triggering odds update: ", error);
      notify(error.message || "Error triggering odds update. Please try again.", "error");
    } finally {
      setIsUpdatingOdds(false);
    }
  };

  const handleFetchScores = async () => {
    setIsFetchingScores(true);
    try {
      const fetchGameScores = httpsCallable(getFunctions(), 'fetchGameScores');
      const result = await fetchGameScores({ week });
      notify(result.data.message, result.data.success ? "success" : "error");
      if (result.data.success) {
        fetchGames(selectedSeason, week);
      }
    } catch (error) {
      console.error("Error fetching scores: ", error);
      notify("An unexpected error occurred. Please try again.", "error");
    } finally {
      setIsFetchingScores(false);
    }
  };

  const handleNotifyNewGames = async () => {
    setIsNotifyingNewGames(true);
    try {
      const notifyNewGames = httpsCallable(getFunctions(), 'notifyNewGamesPublished');
      const result = await notifyNewGames({ week });
      const { sent = 0, failed = 0, recipientCount = 0 } = result.data || {};
      notify(`Sent ${sent} of ${recipientCount} new-games notifications.${failed > 0 ? ` ${failed} failed.` : ''}`, failed > 0 ? 'warning' : 'success');
      setIsNotifyConfirmOpen(false);
    } catch (error) {
      console.error('Error sending new-games notifications:', error);
      notify(error.message || 'Failed to send notifications.', 'error');
    } finally {
      setIsNotifyingNewGames(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const teamName = (id) => teams.find((t) => t.id === id)?.name;

  return (
    <Container maxWidth="lg" sx={{ pb: 6 }}>
      <Typography variant="h4" align="center" gutterBottom>
        Game Management
      </Typography>

      {/* ── Workspace bar: which season/week this page (and Scoring) works on ── */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" alignItems="center">
          <FormControl size="small" sx={{ minWidth: 140, flex: { xs: 1, sm: '0 0 auto' } }}>
            <InputLabel id="games-season-label">Season</InputLabel>
            <MuiSelect
              labelId="games-season-label"
              label="Season"
              value={seasons.includes(selectedSeason) ? selectedSeason : ""}
              onChange={(e) => setSelection(e.target.value, null)}
            >
              {seasons.map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </MuiSelect>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120, flex: { xs: 1, sm: '0 0 auto' } }}>
            <InputLabel id="games-week-label">Week</InputLabel>
            <MuiSelect
              labelId="games-week-label"
              label="Week"
              value={week ?? ""}
              onChange={(e) => setWeek(e.target.value)}
            >
              {WEEK_OPTIONS.map((w) => (
                <MenuItem key={w} value={w}>{w}</MenuItem>
              ))}
            </MuiSelect>
          </FormControl>

          <Box sx={{ flexGrow: 1, flexBasis: { xs: '100%', sm: 0 } }} />

          <Box sx={{ textAlign: { xs: 'left', sm: 'right' }, mr: { xs: 'auto', sm: 0 } }}>
            <Typography variant="caption" color="text.secondary" display="block">Players see</Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {currentSeason ?? '…'} · Week {activeWeek ?? '…'}
            </Typography>
          </Box>
          <Hint title={isActiveSelection ? 'This is already what players see.' : ''}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setIsSetActiveConfirmOpen(true)}
              disabled={!week || !selectedSeason || isActiveSelection}
            >
              Set as active
            </Button>
          </Hint>
          <Tooltip title="Season settings">
            <IconButton aria-label="Season settings" onClick={() => setIsSeasonSettingsOpen(true)}>
              <Settings />
            </IconButton>
          </Tooltip>
        </Stack>
      </Paper>

      {/* ── Games: header + toolbar ── */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        useFlexGap
        alignItems={{ xs: 'stretch', md: 'flex-end' }}
        justifyContent="space-between"
        sx={{ mb: 1.5 }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {week ? `Week ${week} games` : 'Games'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isDataLoading ? 'Loading…' : `${games.length} game${games.length === 1 ? '' : 's'}. Drag the handle to set the order players see.`}
          </Typography>
        </Box>
        <Grid container spacing={1} sx={{ justifyContent: { md: 'flex-end' } }}>
          <Grid size={{ xs: 6, md: 'auto' }}>
            <Button
              variant="contained"
              color="info"
              fullWidth={isMobile}
              onClick={() => setIsImportModalOpen(true)}
              // The importer picks its default week from this week's games,
              // so wait until they're the ones for the week on screen.
              disabled={isDataLoading || !week}
            >
              Import Games
            </Button>
          </Grid>
          <Grid size={{ xs: 6, md: 'auto' }}>
            <Button variant="outlined" fullWidth={isMobile} startIcon={<Add />} onClick={openAddGame} disabled={!week}>
              Add Game
            </Button>
          </Grid>
          <Grid size={{ xs: 6, md: 'auto' }}>
            <Hint title={activeSeasonHint}>
              <Button
                variant="outlined"
                fullWidth={isMobile}
                onClick={handleRefreshGameData}
                disabled={isDataLoading || !week || isUpdatingOdds || !isActiveSeason}
              >
                {isUpdatingOdds ? <CircularProgress size={22} /> : 'Refresh Game Data'}
              </Button>
            </Hint>
          </Grid>
          <Grid size={{ xs: 6, md: 'auto' }}>
            <Hint title={activeSeasonHint}>
              <Button
                variant="outlined"
                fullWidth={isMobile}
                onClick={handleFetchScores}
                disabled={isDataLoading || !week || isFetchingScores || !isActiveSeason}
              >
                {isFetchingScores ? <CircularProgress size={22} /> : 'Fetch Scores'}
              </Button>
            </Hint>
          </Grid>
          <Grid size={{ xs: 6, md: 'auto' }}>
            <Hint title={selectedGameIds.size === 0 ? 'Tick the games to move first' : ''}>
              <Button
                variant="outlined"
                fullWidth={isMobile}
                startIcon={<DriveFileMove />}
                onClick={() => setIsMoveDialogOpen(true)}
                disabled={isDataLoading || selectedGameIds.size === 0}
              >
                {selectedGameIds.size > 0 ? `Move ${selectedGameIds.size} to Week…` : 'Move to Week…'}
              </Button>
            </Hint>
          </Grid>
          <Grid size={{ xs: 6, md: 'auto' }}>
            <Hint title="Pick-the-score games first, then Top 25 matchups, then the rest — each by kickoff time">
              <Button variant="outlined" fullWidth={isMobile} onClick={handleAutoSortGames}
                disabled={games.length === 0} loading={isSortingGames}>
                Auto Sort
              </Button>
            </Hint>
          </Grid>
          <Grid size={{ xs: 6, md: 'auto' }}>
            <Hint title={activeSeasonHint}>
              <Button
                variant="outlined"
                fullWidth={isMobile}
                startIcon={<Sms />}
                onClick={() => setIsNotifyConfirmOpen(true)}
                disabled={!week || isNotifyingNewGames || !isActiveSeason}
              >
                Notify Players
              </Button>
            </Hint>
          </Grid>
        </Grid>
      </Stack>

      {React.createElement(isMobile ? GameCards : GamesTable, {
        games, teams, week, isLoading: isDataLoading,
        onDragEnd, onEnterScores: openScoreModal, onEdit: handleEditGame, onDelete: setGameToDelete,
        selectedIds: selectedGameIds, onToggleSelect: toggleGameSelected,
        onSelectAll: (ids) => setSelectedGameIds(new Set(ids)),
      })}

      <MoveGamesDialog
        open={isMoveDialogOpen}
        season={selectedSeason}
        week={week}
        games={games.filter((g) => selectedGameIds.has(g.id))}
        teams={teams}
        onClose={() => setIsMoveDialogOpen(false)}
        onMoved={handleGamesMoved}
      />

      {/* ── Dialogs ── */}
      <GameFormDialog
        open={formMode !== null}
        mode={formMode || 'add'}
        week={week}
        teams={teams}
        value={gameData}
        onChange={setGameData}
        onSubmit={handleSubmit}
        onClose={closeGameForm}
        isSaving={isSavingGame}
      />

      <Dialog open={isSetActiveConfirmOpen} onClose={() => { if (!isSettingActive) setIsSetActiveConfirmOpen(false); }}>
        <DialogTitle>Show players Week {week} of {selectedSeason}?</DialogTitle>
        <DialogContent>
          <Typography>
            Everyone will see Week {week} of {selectedSeason} on My Picks and the Leaderboard
            {activeWeek ? ` instead of Week ${activeWeek} of ${currentSeason}` : ''}.
            {selectedSeason !== currentSeason ? ` This also makes ${selectedSeason} the active season.` : ''}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsSetActiveConfirmOpen(false)} disabled={isSettingActive}>Cancel</Button>
          <Button variant="contained" onClick={handleSetActive} loading={isSettingActive}>Set as active</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isSeasonSettingsOpen} onClose={() => { if (!isCreatingSeason) setIsSeasonSettingsOpen(false); }} fullWidth maxWidth="xs">
        <DialogTitle>Season settings</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Seasons: {seasons.join(', ') || 'none yet'}. Creating a season starts it at Week 1 and makes it the active season.
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid size={5}>
              <TextField
                label="New season"
                size="small"
                fullWidth
                placeholder="2027"
                value={newSeasonInput}
                onChange={(e) => setNewSeasonInput(e.target.value)}
                slotProps={{ htmlInput: { maxLength: 4, inputMode: 'numeric' } }}
              />
            </Grid>
            <Grid size={7}>
              <Button variant="contained" fullWidth onClick={handleCreateSeason} disabled={!newSeasonInput} loading={isCreatingSeason}>
                Create Season
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsSeasonSettingsOpen(false)} disabled={isCreatingSeason}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isNotifyConfirmOpen} onClose={() => { if (!isNotifyingNewGames) setIsNotifyConfirmOpen(false); }}>
        <DialogTitle>Text players that Week {week} games are posted?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Sends the "new games published" SMS for Week {week} to users who have verified their phone,
            have not opted out via STOP, and have the New Games toggle on in their profile.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Leaderboard-update texts are sent from the Scoring tab when you publish results; kickoff
            reminders go out automatically 2 hours before the first game of each game day.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsNotifyConfirmOpen(false)} disabled={isNotifyingNewGames}>Cancel</Button>
          <Button variant="contained" startIcon={<Sms />} onClick={handleNotifyNewGames} loading={isNotifyingNewGames}>
            Send texts
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(gameToDelete)} onClose={() => { if (!isDeletingGame) setGameToDelete(null); }}>
        <DialogTitle>Delete this game?</DialogTitle>
        <DialogContent>
          {gameToDelete && (
            <Typography>
              {teamName(gameToDelete.awayTeam)} at{' '}
              {teamName(gameToDelete.homeTeam)} (Week {gameToDelete.week}) will be
              removed. This can't be undone.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGameToDelete(null)} disabled={isDeletingGame}>Cancel</Button>
          <Button onClick={() => handleDeleteGame(gameToDelete)} color="error" loading={isDeletingGame}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isScoreModalOpen} onClose={closeScoreModal} fullWidth maxWidth="xs">
        <DialogTitle>Enter Final Score</DialogTitle>
        <DialogContent>
          {selectedGameForScore && (() => {
            const awayValue = scores[selectedGameForScore.id]?.awayScore ?? '';
            const homeValue = scores[selectedGameForScore.id]?.homeScore ?? '';
            const awayValid = isValidScore(awayValue);
            const homeValid = isValidScore(homeValue);
            return (
              <Grid container spacing={2} style={{ marginTop: '10px' }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="h6">{teamName(selectedGameForScore.awayTeam)}</Typography>
                  <TextField
                    autoFocus
                    margin="dense"
                    label="Away Score"
                    type="number"
                    fullWidth
                    value={awayValue}
                    onChange={(e) => handleScoreChange(selectedGameForScore.id, 'awayScore', e.target.value)}
                    inputProps={{ min: 0, step: 1, inputMode: 'numeric', pattern: '[0-9]*' }}
                    error={awayValue !== '' && !awayValid}
                    helperText={awayValue !== '' && !awayValid ? 'Enter a non-negative whole number' : ' '}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="h6">{teamName(selectedGameForScore.homeTeam)}</Typography>
                  <TextField
                    margin="dense"
                    label="Home Score"
                    type="number"
                    fullWidth
                    value={homeValue}
                    onChange={(e) => handleScoreChange(selectedGameForScore.id, 'homeScore', e.target.value)}
                    inputProps={{ min: 0, step: 1, inputMode: 'numeric', pattern: '[0-9]*' }}
                    error={homeValue !== '' && !homeValid}
                    helperText={homeValue !== '' && !homeValid ? 'Enter a non-negative whole number' : ' '}
                  />
                </Grid>
              </Grid>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeScoreModal} disabled={isSavingScores}>Cancel</Button>
          <Button
            onClick={handleSaveScores}
            color="primary"
            loading={isSavingScores}
            disabled={
              !selectedGameForScore ||
              !isValidScore(scores[selectedGameForScore.id]?.awayScore ?? '') ||
              !isValidScore(scores[selectedGameForScore.id]?.homeScore ?? '')
            }
          >
            Save Scores
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbarOpen} autoHideDuration={snackbarAction ? 10000 : 6000} onClose={handleCloseSnackbar}>
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: '100%', alignItems: 'center' }}
          action={snackbarAction && (
            <Button color="inherit" size="small" onClick={snackbarAction.onClick}>{snackbarAction.label}</Button>
          )}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <ImportGamesModal
        open={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        season={selectedSeason}
        week={week}
        teams={teams}
        existingGames={games}
        onGamesImported={() => fetchGames(selectedSeason, week)}
      />
    </Container>
  );
}

export default Admin;
